import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import AppDialog from '../../../../../../../components/common/AppDialog';
import AppTextInput from '../../../../../../../components/common/AppTextInput';
import {
  BranchData,
  ClientDataType,
  SubBranchData,
} from '../../../../../../../context/clients/ClientsContextProvider';
import { SVG } from '../../../../../../../constants/assetLists';
import AppMUIAutocomplete from '../../../../../../../components/common/AppMUIAutoComplete';
import BranchTreeAutocomplete from '../../../../../../../components/common/BranchTreeAutocomplete';
import {
  BranchNodeFormValues,
  ChecklistFormValues,
  FolderValues,
  ChecklistSidebarSelection,
  NetworkDetailsFormValues,
  NetworkNodeFieldPath,
  NetworkNodeFormValues,
  ShiftFormValues,
  SubBranchNodeFormValues,
  findFieldPathForNodeId,
  isGeneralChecklistFolder,
} from '../../../types';
import { findParentBranchId } from '../../../../../../../utils';

type ShiftFolderOption = {
  label: string;
  value: string;
  icon: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  selectedFieldPath: NetworkNodeFieldPath;
  editingChecklistId?: string;
  sidebarSelection: ChecklistSidebarSelection | null;
};

// ─── mappers ────────────────────────────────────────────────────────────────

const mapSubBranchToTree = (
  sub: SubBranchNodeFormValues,
  branchId: string,
  clientId: string,
): SubBranchData => ({
  id: sub.id,
  name: sub.name,
  icon: '',
  isClient: false,
  isBranch: false,
  isSubBranch: true,
  clientId,
  branchId,
});

const mapBranchToTree = (
  branch: BranchNodeFormValues,
  clientId: string,
): BranchData => ({
  id: branch.id,
  name: branch.name,
  icon: '',
  isClient: false,
  isBranch: true,
  isSubBranch: false,
  clientId,
  subBranches: (branch.subBranches ?? []).map((sb) =>
    mapSubBranchToTree(sb, branch.id, clientId),
  ),
});

const mapNetworkNodeToClientTree = (
  client: NetworkNodeFormValues,
): ClientDataType => ({
  id: client.id,
  name: client.name,
  icon: '',
  isClient: true,
  isBranch: false,
  isSubBranch: false,
  branches: client.branches.map((b) => mapBranchToTree(b, client.id)),
});

// ─── helpers ─────────────────────────────────────────────────────────────────

const getCurrentNodeId = (
  network: NetworkNodeFormValues[],
  path: NetworkNodeFieldPath,
): string | null => {
  const parts = path.split('.');
  if (parts.length === 2) return network[parseInt(parts[1])]?.id ?? null;
  if (parts.length === 4)
    return (
      network[parseInt(parts[1])]?.branches[parseInt(parts[3])]?.id ?? null
    );
  if (parts.length === 6)
    return (
      network[parseInt(parts[1])]?.branches[parseInt(parts[3])]?.subBranches[
        parseInt(parts[5])
      ]?.id ?? null
    );
  return null;
};

const buildShiftFolderOptions = (
  network: NetworkNodeFormValues[],
  nodeId: string | null,
): ShiftFolderOption[] => {
  if (!nodeId) return [];
  const targetPath = findFieldPathForNodeId(network, nodeId);
  if (!targetPath) return [];

  const parts = targetPath.split('.');
  let targetNode:
    | NetworkNodeFormValues
    | BranchNodeFormValues
    | SubBranchNodeFormValues
    | undefined;

  if (parts.length === 2) {
    targetNode = network[parseInt(parts[1])];
  } else if (parts.length === 4) {
    targetNode = network[parseInt(parts[1])]?.branches[parseInt(parts[3])];
  } else if (parts.length === 6) {
    targetNode =
      network[parseInt(parts[1])]?.branches[parseInt(parts[3])]?.subBranches[
        parseInt(parts[5])
      ];
  }

  if (!targetNode) return [];

  const shifts: ShiftFormValues[] = targetNode.shifts ?? [];
  const folders: FolderValues[] = targetNode.checklistFolders ?? [];

  return [
    ...shifts.map((s) => ({
      label: `${s.name} ${s.from} - ${s.to}`,
      value: s.id,
      icon: SVG.clockIcon,
    })),
    ...folders
      .filter((f) => !isGeneralChecklistFolder(f))
      .map((f) => ({ label: f.name, value: f.id, icon: SVG.folderBlackIcon })),
  ];
};

// ─── component ───────────────────────────────────────────────────────────────

function DuplicateFolderDialog({
  open,
  onClose,
  onSaved,
  selectedFieldPath,
  editingChecklistId,
  sidebarSelection,
}: Props) {
  const { watch, getValues, setValue } =
    useFormContext<NetworkDetailsFormValues>();

  const network = watch('network') || [];

  const [titleValue, setTitleValue] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  const [selectedSubBranchId, setSelectedSubBranchId] = useState<string | null>(
    null,
  );
  const [selectedShiftFolderOption, setSelectedShiftFolderOption] =
    useState<ShiftFolderOption | null>(null);

  // ── init / reset on open ──────────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      setTitleValue('');
      setSelectedBranchId(null);
      setSelectedSubBranchId(null);
      setSelectedShiftFolderOption(null);
      return;
    }

    const checklists: ChecklistFormValues[] =
      getValues(`${selectedFieldPath}.checklists`) ?? [];
    const source = checklists.find((c) => c.id === editingChecklistId);
    if (source) setTitleValue(`${source.title} - עותק`);

    const parts = selectedFieldPath.split('.');
    const clientIdx = parseInt(parts[1]);
    const currentClient = network[clientIdx];

    if (parts.length >= 4) {
      const branchIdx = parseInt(parts[3]);
      const branch = currentClient?.branches[branchIdx];
      setSelectedBranchId(branch?.id ?? null);

      if (parts.length === 6) {
        const subBranchIdx = parseInt(parts[5]);
        const subBranch = branch?.subBranches?.[subBranchIdx];
        setSelectedSubBranchId(subBranch?.id ?? null);

        if (sidebarSelection) {
          const effectiveId = subBranch?.id ?? branch?.id ?? null;
          const opts = buildShiftFolderOptions(network, effectiveId);
          setSelectedShiftFolderOption(
            opts.find((o) => o.value === sidebarSelection.id) ?? null,
          );
        }
      } else {
        setSelectedSubBranchId(null);
        if (sidebarSelection && branch?.id) {
          const opts = buildShiftFolderOptions(network, branch.id);
          setSelectedShiftFolderOption(
            opts.find((o) => o.value === sidebarSelection.id) ?? null,
          );
        }
      }
    } else {
      // At client level — fall back to the node ID for shift/folder init
      const currentId = getCurrentNodeId(network, selectedFieldPath);
      setSelectedBranchId(currentId);
      setSelectedSubBranchId(null);

      if (sidebarSelection && currentId) {
        const opts = buildShiftFolderOptions(network, currentId);
        setSelectedShiftFolderOption(
          opts.find((o) => o.value === sidebarSelection.id) ?? null,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── filtered client tree (current client only) ────────────────────────────
  const filteredClients = useMemo((): ClientDataType[] => {
    const networkIndex = parseInt(selectedFieldPath.split('.')[1]);
    const client = network[networkIndex];
    return client ? [mapNetworkNodeToClientTree(client)] : [];
  }, [network, selectedFieldPath]);

  // ── effective target: subBranch if selected, else branch ──────────────────
  const effectiveNodeId =
    selectedSubBranchId ?? selectedBranchId ?? filteredClients[0]?.id ?? null;

  // ── target shift / folder options ─────────────────────────────────────────
  const targetOptions = useMemo(
    () => buildShiftFolderOptions(network, effectiveNodeId),
    [network, effectiveNodeId],
  );

  // reset shift/folder selection if it no longer exists in the new target
  useEffect(() => {
    if (
      selectedShiftFolderOption &&
      !targetOptions.some((o) => o.value === selectedShiftFolderOption.value)
    ) {
      setSelectedShiftFolderOption(null);
    }
  }, [targetOptions, selectedShiftFolderOption]);

  // ── handlers ──────────────────────────────────────────────────────────────

  const handleBranchTreeChange = (
    nodes: (ClientDataType | BranchData | SubBranchData)[],
  ) => {
    const incomingBranchId = nodes.find((n) => n.isBranch)?.id ?? null;
    const incomingSubBranchId = nodes.find((n) => n.isSubBranch)?.id ?? null;

    let newBranchId = incomingBranchId;
    let newSubBranchId = incomingSubBranchId;

    // Branch was explicitly removed (was selected, now gone)
    // Clear subBranch too — must check this before the auto-add rule below
    if (selectedBranchId && !incomingBranchId) {
      newSubBranchId = null;
    }
    // SubBranch newly selected without its parent branch → auto-add parent
    else if (incomingSubBranchId && !incomingBranchId) {
      newBranchId = findParentBranchId(network, incomingSubBranchId);
    }
    // Branch swapped while a subBranch is selected → clear subBranch if it
    // doesn't belong to the new branch
    else if (incomingBranchId && incomingSubBranchId) {
      const parentBranchId = findParentBranchId(network, incomingSubBranchId);
      if (parentBranchId !== incomingBranchId) {
        newSubBranchId = null;
      }
    }

    setSelectedBranchId(newBranchId);
    setSelectedSubBranchId(newSubBranchId);
    setSelectedShiftFolderOption(null);
  };

  const handleOnClose = () => onClose();

  const handleSave = () => {
    if (!titleValue || !effectiveNodeId || !selectedShiftFolderOption) return;

    const allChecklists: ChecklistFormValues[] =
      getValues(`${selectedFieldPath}.checklists`) ?? [];
    const source = allChecklists.find((c) => c.id === editingChecklistId);
    if (!source) return;

    const targetPath = findFieldPathForNodeId(network, effectiveNodeId);
    if (!targetPath) return;

    const targetChecklists: ChecklistFormValues[] =
      getValues(`${targetPath}.checklists`) ?? [];
    const targetShifts: ShiftFormValues[] =
      getValues(`${targetPath}.shifts`) ?? [];
    const isShift = targetShifts.some(
      (s) => s.id === selectedShiftFolderOption.value,
    );

    const cloned: ChecklistFormValues = {
      ...source,
      id: crypto.randomUUID(),
      title: titleValue,
      shiftId: isShift ? selectedShiftFolderOption.value : undefined,
      folderId: !isShift ? selectedShiftFolderOption.value : undefined,
      departments: [],
    };

    setValue(`${targetPath}.checklists`, [...targetChecklists, cloned], {
      shouldValidate: true,
      shouldDirty: true,
    });

    onSaved?.();
    handleOnClose();
  };

  // ids that represent real branch/subBranch nodes — excludes the client's
  // own id (used internally as a fallback when duplicating at the client
  // level) so it never leaks into BranchTreeAutocomplete's value and shows
  // up as a spurious, non-selectable chip there
  const validTreeIds = useMemo(() => {
    const ids = new Set<string>();
    filteredClients.forEach((client) => {
      client.branches.forEach((branch) => {
        ids.add(branch.id);
        branch.subBranches.forEach((sb) => ids.add(sb.id));
      });
    });
    return ids;
  }, [filteredClients]);

  const treeValue = [selectedBranchId, selectedSubBranchId].filter(
    (id): id is string => Boolean(id) && validTreeIds.has(id as string),
  );

  const isSaveDisabled =
    !titleValue.trim() || !effectiveNodeId || !selectedShiftFolderOption;

  return (
    <AppDialog
      showModel={open}
      onClose={handleOnClose}
      showCloseIcon={false}
      containerClasses="!rounded-[12px] !pt-3 !border-none !overflow-visible"
      headerClasses="!px-0"
      width={480}
      titleField={<p className="m-0 text-base font-medium">שכפול צ'קליסט</p>}
      showHeaderLine={false}
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-3">
          <AppTextInput
            className="border-0"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
          />

          <BranchTreeAutocomplete
            disablePortal={false}
            required
            label="סניף"
            showClientChip
            showSelectedTags
            allClients={filteredClients}
            value={treeValue}
            onChange={handleBranchTreeChange}
          />

          <AppMUIAutocomplete
            disablePortal={false}
            label="משמרת / תיקייה"
            size="small"
            options={targetOptions}
            getOptionLabel={(o) => (o as ShiftFolderOption).label}
            fullWidth
            required
            value={selectedShiftFolderOption}
            onChange={(_e, val) =>
              setSelectedShiftFolderOption(val as ShiftFolderOption | null)
            }
            isOptionEqualToValue={(opt, val) =>
              (opt as ShiftFolderOption).value ===
              (val as ShiftFolderOption).value
            }
            renderValue={(value) =>
              value && (
                <div className="flex items-center gap-2">
                  <img
                    src={(value as ShiftFolderOption)?.icon}
                    alt="icon"
                    className="w-[20px] h-[20px] object-contain"
                  />
                  <span className="text-base text-textColor">
                    {(value as ShiftFolderOption)?.label}
                  </span>
                </div>
              )
            }
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              const opt = option as ShiftFolderOption;
              return (
                <li key={key} {...optionProps}>
                  <img
                    src={opt?.icon}
                    alt="icon"
                    className="w-[20px] h-[20px] object-contain"
                  />
                  <span className="ms-2 text-base text-textColor">
                    {opt?.label}
                  </span>
                </li>
              );
            }}
          />
        </div>

        <div className="flex justify-end">
          <div className="flex items-center gap-5">
            <button
              type="button"
              className="text-base font-medium text-darkGray"
              onClick={handleOnClose}
            >
              ביטול
            </button>
            <button
              type="button"
              disabled={isSaveDisabled}
              className={`text-base font-medium ${isSaveDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-darkGreen'}`}
              onClick={handleSave}
            >
              שמירה
            </button>
          </div>
        </div>
      </div>
    </AppDialog>
  );
}

export default DuplicateFolderDialog;
